import type { FormikTouched } from "formik";
import { z } from "zod";
import { createFormikFields } from "@components/forms/fields/createFormikFields";
import {
    choiceFieldsPlaygroundInitialTouched,
    choiceFieldsPlaygroundInitialValues,
    choiceFieldsPlaygroundSchema,
} from "./formFieldsPlaygroundChoice.schema";
import {
    labellessFieldsPlaygroundInitialTouched,
    labellessFieldsPlaygroundInitialValues,
    labellessFieldsPlaygroundSchema,
} from "./formFieldsPlaygroundLabelless.schema";

const paymentKinds = ["check", "deposit"] as const;

const pickedAccount = z
    .number()
    .nullable()
    .refine((id): boolean => id !== null, { message: "Pick an account" });
const pickedCounterparty = z
    .string()
    .nullable()
    .refine((id): boolean => id !== null, { message: "Pick a payee" });
const pickedLedgerAccount = z
    .string()
    .nullable()
    .refine((id): boolean => id !== null, {
        message: "Pick an income account",
    });
const pausedKinds: ReadonlySet<string> = new Set(["check"]);
const dateRange = z.object({
    start: z.string().optional(),
    end: z.string().optional(),
});
const lastYear = { start: "2025-01-01", end: "2025-12-31" };

export const paymentKindOptions = [
    { value: "check", label: "Check" },
    { value: "deposit", label: "Deposit" },
] as const;

/** One kit field per state column: normal, optional, tip, error, disabled. */
export const formFieldsPlaygroundSchema = z.object({
    text: z.object({
        normal: z.string().trim().min(1, "Enter a payee"),
        optional: z.string().optional(),
        tip: z.string().trim().min(1, "Enter a reference"),
        error: z.string().email("Enter a valid email"),
        disabled: z.string(),
    }),
    textArea: z.object({
        normal: z.string().trim().min(1, "Add a memo"),
        optional: z.string().nullable(),
        tip: z.string(),
        error: z.string().max(20, "Keep the memo under 20 characters"),
        disabled: z.string(),
    }),
    date: z.object({
        normal: z.string().min(1, "Pick a date"),
        optional: z.string().optional(),
        tip: z.string().nullable(),
        error: z.string().refine((date) => date >= "2027-01-01", {
            message: "Pick a date in 2027 or later",
        }),
        disabled: z.string(),
    }),
    dateRange: z.object({
        normal: dateRange,
        optional: dateRange.optional(),
        tip: dateRange.nullable(),
        error: dateRange.refine((range) => (range.start ?? "") >= "2027", {
            message: "Pick a range in 2027 or later",
        }),
        disabled: dateRange,
    }),
    money: z.object({
        normal: z.number().positive("Enter an amount above zero"),
        optional: z.number().optional(),
        tip: z.number().nullable(),
        error: z.number().min(100, "Amounts start at $100"),
        disabled: z.number(),
    }),
    count: z.object({
        normal: z.number().int().min(1, "Repeat at least once"),
        optional: z.number().int().optional(),
        tip: z.number().int(),
        error: z.number().int().min(2, "Repeat at least twice"),
        disabled: z.number().int(),
    }),
    select: z.object({
        normal: z.enum(paymentKinds),
        optional: z.enum(paymentKinds).optional(),
        tip: z.enum(paymentKinds),
        error: z.enum(paymentKinds).refine((kind) => !pausedKinds.has(kind), {
            message: "Checks are paused this month",
        }),
        disabled: z.enum(paymentKinds),
    }),
    financialAccount: z.object({
        normal: pickedAccount,
        optional: z.number().optional(),
        tip: z.array(z.number()),
        error: pickedAccount,
        disabled: z.number().nullable(),
    }),
    counterparty: z.object({
        normal: pickedCounterparty,
        optional: z.string().nullable(),
        tip: z.array(z.string()),
        error: pickedCounterparty,
        disabled: z.string().nullable(),
    }),
    ledgerAccount: z.object({
        normal: pickedLedgerAccount,
        optional: z.string().nullable(),
        tip: z.string().optional(),
        error: pickedLedgerAccount,
        disabled: z.string().nullable(),
    }),
    entity: z.object({
        normal: z.array(z.number()).min(1, "Pick an entity"),
        optional: z.array(z.number()),
        tip: z.array(z.number()),
        error: z.array(z.number()).min(1, "Pick an entity"),
        disabled: z.array(z.number()),
    }),
    emails: z.object({
        normal: z.array(z.string()).min(1, "Add an email"),
        optional: z.array(z.string()),
        tip: z.array(z.string()),
        error: z.array(z.string()).max(1, "Invite one person at a time"),
        disabled: z.array(z.string()),
    }),
    phone: z.object({
        normal: z.string().regex(/^\d{10}$/, "Enter a 10-digit number"),
        optional: z.string().optional(),
        tip: z.string(),
        error: z.string().regex(/^\d{10}$/, "Enter a 10-digit number"),
        disabled: z.string(),
    }),
    ...choiceFieldsPlaygroundSchema.shape,
    ...labellessFieldsPlaygroundSchema.shape,
});

export type FormFieldsPlaygroundValues = z.infer<
    typeof formFieldsPlaygroundSchema
>;

export const formFieldsPlaygroundFields =
    createFormikFields<FormFieldsPlaygroundValues>();

export const formFieldsPlaygroundInitialValues: FormFieldsPlaygroundValues = {
    text: {
        normal: "Acme Supplies",
        tip: "",
        error: "billing@acme",
        disabled: "Locked payee",
    },
    textArea: {
        normal: "September rent",
        optional: null,
        tip: "",
        error: "Paid by check for the September office rent",
        disabled: "Imported from the bank",
    },
    date: {
        normal: "2026-09-24",
        optional: "2026-10-01",
        tip: null,
        error: "2026-09-24",
        disabled: "2026-09-01",
    },
    dateRange: {
        normal: lastYear,
        optional: lastYear,
        tip: null,
        error: lastYear,
        disabled: lastYear,
    },
    money: {
        normal: 1250,
        optional: 99.5,
        tip: null,
        error: 40,
        disabled: 500,
    },
    count: {
        normal: 3,
        tip: 1,
        error: 1,
        disabled: 12,
    },
    select: {
        normal: "check",
        tip: "deposit",
        error: "check",
        disabled: "deposit",
    },
    financialAccount: {
        normal: null,
        tip: [],
        error: null,
        disabled: null,
    },
    counterparty: {
        normal: null,
        optional: null,
        tip: [],
        error: null,
        disabled: null,
    },
    ledgerAccount: {
        normal: null,
        optional: null,
        error: null,
        disabled: null,
    },
    entity: {
        normal: [],
        optional: [],
        tip: [],
        error: [],
        disabled: [],
    },
    emails: {
        normal: ["ap@acme.co"],
        optional: [],
        tip: [],
        error: ["ap@acme.co", "ops@acme.co"],
        disabled: ["owner@acme.co"],
    },
    phone: {
        normal: "4155550100",
        tip: "",
        error: "415555",
        disabled: "6505550199",
    },
    ...choiceFieldsPlaygroundInitialValues,
    ...labellessFieldsPlaygroundInitialValues,
};

/** The error column starts touched so its message shows on load. */
export const formFieldsPlaygroundInitialTouched: FormikTouched<FormFieldsPlaygroundValues> =
    {
        text: { error: true },
        textArea: { error: true },
        date: { error: true },
        dateRange: { error: { start: true } },
        money: { error: true },
        count: { error: true },
        select: { error: true },
        financialAccount: { error: true },
        counterparty: { error: true },
        ledgerAccount: { error: true },
        entity: { error: true },
        emails: { error: true },
        phone: { error: true },
        ...choiceFieldsPlaygroundInitialTouched,
        ...labellessFieldsPlaygroundInitialTouched,
    };
